import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { 
  Stack, 
  Text, 
  Card, 
  Button, 
  Group, 
  Badge,
  Checkbox,
  ActionIcon,
  SegmentedControl,
  Grid,
  Paper,
  ScrollArea,
  Modal,
  Box,
  TextInput,
  Select,
  Indicator
} from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import { useQueryClient } from '@tanstack/react-query';
import { Check, Trash2, X } from 'lucide-react';
import { 
  Clock, 
  ChevronLeft, 
  ChevronRight
} from 'lucide-react';
import { 
  DndContext, 
  DragOverlay,
  rectIntersection,
  pointerWithin,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  MeasuringStrategy
} from '@dnd-kit/core';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { useTodos, useCreateTodo, useUpdateTodo, useTodoLists, useDeleteTodo } from '../../hooks/useTodos';
import { usePremiumFeatures } from '../../contexts/PremiumContext';
import { useNavigation } from '../../contexts/NavigationContext';
import type { TodoItem as TodoItemType, CreateTodoRequest, TodoList, UpdateTodoRequest } from '../../types/todo';
import { SuperTaskToolbar } from '../SuperTaskToolbar';
import { Modal as CustomModal } from '../Modal';
import { EditTodoForm, TaskTitleInput } from '../EditTodoForm';
import { useListManagement } from '../../hooks/useListManagement';
import { ListManagementModals } from '../ListManagementModals';
import { SectionTodoEntry } from '../SectionTodoEntry';
import { usePersistedState } from '../../hooks/usePersistedState';
import dayjs from 'dayjs';
// Reuse the main todo item card component
import { TodoItemComponent } from '../TodoItem';
import { CalendarPreviewDemo } from '../CalendarPreviewDemo';

type ViewType = 'month' | 'week';

// Minimum width for each day card in week view (in pixels)
const WEEK_DAY_MIN_WIDTH = 320;
// Gutter reserved for vertical scrollbar; used to visually balance content
const SCROLLBAR_GUTTER_PX = 6;

// Shared sort: Incomplete first; within group: timed (earlier first) then untimed; preserve original order for ties
const sortTasksForCalendar = (tasks: TodoItemType[]) => {
  const indexMap = new Map<number, number>(tasks.map((t, i) => [t.id, i]));
  return tasks.slice().sort((a, b) => {
    // Incomplete before completed
    if (a.isCompleted !== b.isCompleted) return a.isCompleted ? 1 : -1;

    const aHasTime = Boolean(a.dueTime && a.dueDate);
    const bHasTime = Boolean(b.dueTime && b.dueDate);

    // Timed before untimed
    if (aHasTime && !bHasTime) return -1;
    if (!aHasTime && bHasTime) return 1;

    // If both timed, sort by due time ascending
    if (aHasTime && bHasTime) {
      const at = dayjs(a.dueDate).valueOf();
      const bt = dayjs(b.dueDate).valueOf();
      if (at !== bt) return at - bt;
    }

    // Stable fallback by original position
    return (indexMap.get(a.id)! - indexMap.get(b.id)!);
  });
};

// Draggable task component
const DraggableTask: React.FC<{ 
  task: TodoItemType; 
  isDragging?: boolean;
  compact?: boolean;
  onOpenEdit?: (task: TodoItemType) => void;
  onToggleComplete?: (task: TodoItemType) => void;
  showCheckbox?: boolean;
  stripeColor?: string;
}> = ({ task, isDragging, compact = false, onOpenEdit, onToggleComplete, showCheckbox = true, stripeColor }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
  } = useDraggable({
    id: task.id,
    data: task
  });

  const style = {
    // Don't apply transform to the original element during drag
    transform: isDragging ? 'none' : CSS.Translate.toString(transform),
    opacity: isDragging ? 0.3 : 1,
    cursor: 'grab',
    pointerEvents: isDragging ? ('none' as const) : ('auto' as const)
  };

  // Removed priority-to-color mapping for compact month view;
  // only High should stand out. Medium/Low render as normal text.

  if (compact) {
    return (
      <Paper
        ref={setNodeRef}
        style={{
          ...style,
          padding: '2px 6px',
          backgroundColor: 'var(--app-surface)',
          borderRadius: 'var(--mantine-radius-sm)',
          border: '1px solid var(--app-border)',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          overflow: 'hidden',
        }}
        {...attributes}
        {...listeners}
        onClick={(e) => {
          e.stopPropagation();
          onOpenEdit?.(task);
        }}
      >
        {stripeColor && (
          <span
            aria-hidden
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: stripeColor,
              flexShrink: 0,
            }}
          />
        )}
        {showCheckbox && (
          <Checkbox
            checked={!!task.isCompleted}
            size="xs"
            radius="sm"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => {
              e.stopPropagation();
              onToggleComplete?.(task);
            }}
          />
        )}
        <Text
          size="sm" // Increased from xs
          style={{
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            textDecoration: task.isCompleted ? 'line-through' : undefined,
            opacity: task.isCompleted ? 0.6 : 1,
            color: task.priority === 'High' ? 'var(--app-danger-text)' : 'inherit',
          }}
        >
          {task.title}
        </Text>
      </Paper>
    );
  }

  return (
    <Card 
      ref={setNodeRef}
      style={{
        ...style,
        borderLeft: stripeColor ? `3px solid ${stripeColor}` : undefined,
        paddingLeft: stripeColor ? 10 : undefined
      }}
      {...attributes}
      {...listeners}
      p="xs" 
      withBorder
      opacity={task.isCompleted ? 0.6 : 1}
      onClick={(e) => {
        e.stopPropagation();
        onOpenEdit?.(task);
      }}
    >
      <Group justify="space-between" align="center" wrap="nowrap" gap="xs">
        {showCheckbox && (
          <Checkbox
            checked={!!task.isCompleted}
            size="sm"
            radius="sm"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => {
              e.stopPropagation();
              onToggleComplete?.(task);
            }}
          />
        )}
        <Text 
          size="sm" 
          style={{ 
            textDecoration: task.isCompleted ? 'line-through' : undefined,
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {task.title}
        </Text>
        {(task.priority === 'Low' || task.priority === 'High') && (
          <Badge
            size="xs"
            variant="light"
            color={task.priority === 'High' ? 'red' : 'gray'}
          >
            {task.priority}
          </Badge>
        )}
      </Group>
      {task.dueTime && (
        <Text size="xs" style={{ color: 'var(--app-text-muted)' }} mt={4}>
          <Clock size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />
          {dayjs(task.dueDate).format('h:mm A')}
        </Text>
      )}
    </Card>
  );
};

// Droppable calendar cell
const DroppableCalendarCell: React.FC<{
  date: Date;
  children: React.ReactNode;
  isCurrentMonth: boolean;
  isToday: boolean;
  onDayClick: () => void;
}> = ({ date, children, isCurrentMonth, isToday, onDayClick }) => {
  const dateStr = dayjs(date).format('YYYY-MM-DD');
  const { isOver, setNodeRef } = useDroppable({
    id: dateStr,
    data: { date: dateStr }
  });

  return (
    <Paper
      ref={setNodeRef}
      p="xs"
      style={{
        minHeight: '140px', // Increased height
        cursor: 'pointer',
        backgroundColor: isToday 
          ? 'var(--app-calendar-today-bg)' 
          : isOver 
          ? 'var(--app-success-bg)' 
          : isCurrentMonth 
          ? 'var(--app-surface)' 
          : 'var(--app-hover)',
        opacity: isCurrentMonth ? 1 : 0.9,
        border: isToday 
          ? '2px solid var(--app-primary)' 
          : isOver 
          ? '2px solid var(--app-success-text)'
          : isCurrentMonth 
          ? '1px solid var(--app-border)'
          : '1px solid var(--app-text-muted)',
        transition: 'all 0.2s ease',
        boxShadow: isToday 
          ? '0 2px 12px rgba(59, 130, 246, 0.4)' 
          : undefined
      }}
      onClick={onDayClick}
    >
      {children}
    </Paper>
  );
};

// Droppable week day cell component
const DroppableWeekDayCell: React.FC<{
  date: Date;
  isToday: boolean;
  tasks: TodoItemType[];
  onOpenEdit: (task: TodoItemType) => void;
  onToggleComplete: (task: TodoItemType) => void;
  currentFilters: { assignee: string; list: string | number; completed: 'today' | 'all' | 'hidden'; tags: string[]; search: string };
  onFiltersChange: (f: { assignee: string; list: string | number; completed: 'today' | 'all' | 'hidden'; tags: string[]; search: string }) => void;
  onDayClick: (date: Date) => void;
  todoLists: TodoList[];
}> = ({ date, isToday, tasks, onOpenEdit, onToggleComplete, currentFilters, onFiltersChange, onDayClick, todoLists }) => {
  const dateStr = dayjs(date).format('YYYY-MM-DD');
  const { isOver, setNodeRef } = useDroppable({
    id: dateStr,
    data: { date: dateStr }
  });

  return (
    <Box style={{ flex: `0 0 ${WEEK_DAY_MIN_WIDTH}px`, minWidth: WEEK_DAY_MIN_WIDTH }}>
      <Paper 
        ref={setNodeRef}
        p="md"
        style={{
          minHeight: '400px',
          minWidth: WEEK_DAY_MIN_WIDTH,
          width: '100%',
          // Reduce horizontal padding to give items more room
          paddingLeft: 10,
          paddingRight: 10,
          backgroundColor: isToday 
            ? 'var(--app-calendar-today-bg)' 
            : isOver 
            ? 'var(--app-success-bg)'
            : 'var(--app-surface)',
          border: isToday 
            ? '2px solid var(--app-primary)' 
            : isOver
            ? '2px solid var(--app-success-text)'
            : '1px solid var(--app-border)',
          transition: 'all 0.2s ease',
          boxShadow: isToday 
            ? '0 2px 12px rgba(59, 130, 246, 0.4)' 
            : undefined
        }}
        
        onClick={() => onDayClick(date)}
      >
        <Text size="sm" fw={700} mb="xs" style={{ color: 'var(--app-text-secondary)' }}>
          {date.toLocaleDateString(undefined, { weekday: 'long' })}
        </Text>
        <Text 
          size="lg" 
          fw={isToday ? 800 : 500} 
          mb="md"
          style={{ 
            color: isToday ? 'var(--app-primary)' : 'var(--app-text-primary)',
          }}
        >
          {date.getDate()}
        </Text>
  <ScrollArea h={280} data-vertical-scroll-zone="true" offsetScrollbars scrollbarSize={SCROLLBAR_GUTTER_PX} type="always">
          <Box style={{ paddingLeft: SCROLLBAR_GUTTER_PX }}>
            <Stack gap="xs">
              {sortTasksForCalendar(tasks).map(task => {
                const listColor = currentFilters.list === 'all' 
                  ? (todoLists.find((l: TodoList) => l.id === task.listId)?.color) 
                  : undefined;
                return (
                  <DraggableTask 
                    key={task.id} 
                    task={task} 
                    onOpenEdit={onOpenEdit} 
                    onToggleComplete={onToggleComplete}
                    stripeColor={listColor}
                  />
                );
              })}
              {tasks.length === 0 && (
                <Text size="sm" style={{ color: 'var(--app-text-muted)' }} ta="center">
                  No tasks
                </Text>
              )}
            </Stack>
          </Box>
        </ScrollArea>
        <div style={{ marginTop: 8 }} onClick={(e) => e.stopPropagation()}>
          <SectionTodoEntry
            sectionType="noDueDate"
            currentFilters={currentFilters}
            onFiltersChange={onFiltersChange}
            hideIcon={true}
            dueDateISO={dayjs(date).format('YYYY-MM-DD')}
          />
        </div>
      </Paper>
    </Box>
  );
};

export const CalendarMode: React.FC = () => {
  const { data: todos = [] } = useTodos();
  const { data: todoLists = [] } = useTodoLists();
  const { isPremium, isLoading: premiumLoading } = usePremiumFeatures();
  const { isMobile } = useNavigation();
  const queryClient = useQueryClient();
  const createTodoMutation = useCreateTodo();
  const updateTodoMutation = useUpdateTodo();
  const deleteTodoMutation = useDeleteTodo();
  
  // Add filter state for consistency with TodosMode
  const [todoFilters, setTodoFilters] = usePersistedState<{ assignee: string; list: string | number; completed: 'today' | 'all' | 'hidden'; tags: string[]; search: string }>('symphonytodo-filters', {
    assignee: 'any',
    list: 'all',
    completed: 'all' as 'today' | 'all' | 'hidden',
    tags: [] as string[],
    search: ''
  });

  // Centralized list management
  const listManagement = useListManagement({
    onListCreated: (newList) => {
      // Auto-select newly created list
      setTodoFilters(f => ({ ...f, list: newList.id }));
    },
    onListDeleted: () => {
      // Reset filters to show all lists after deletion
      setTodoFilters({ ...todoFilters, list: 'all' });
    },
  });

  // Calculate selected list for coloring and management
  const selectedList = todoLists.find(list => list.id === todoFilters.list) || null;
  
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewType, setViewType] = useState<ViewType>('month');
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [dayModalOpen, setDayModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [draggedTask, setDraggedTask] = useState<TodoItemType | null>(null);
  const [editingTodo, setEditingTodo] = useState<TodoItemType | null>(null);
  const [editFormData, setEditFormData] = useState<UpdateTodoRequest | null>(null);
  const [newTask, setNewTask] = useState({
    title: '',
    priority: 'Medium' as 'High' | 'Medium' | 'Low',
    dueDate: null as Date | null,
    dueTime: false
  });

  // Ref to the horizontally scrollable week container
  const weekScrollRef = useRef<HTMLDivElement | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 3,
      },
    })
  );

  // Enable horizontal scrolling of the week row using the mouse wheel from anywhere on screen,
  // without interfering with modals or vertical scroll zones
  useEffect(() => {
    const handler = (e: WheelEvent) => {
      if (viewType !== 'week') return; // Only apply in week view

      // If any modal/dialog is open, don't hijack wheel
      if (dayModalOpen || !!editingTodo || createModalOpen || listManagement.showCreateListForm || listManagement.showEditListForm || listManagement.showListInfoDialog) {
        return;
      }

      const container = weekScrollRef.current;
      if (!container) return;

      // If the event originated inside a vertical scroll zone, only hijack when that zone
      // cannot scroll further in the wheel direction, allowing the week row to scroll instead
      const target = e.target as HTMLElement | null;
      if (target) {
        const zone = target.closest('[data-vertical-scroll-zone]') as HTMLElement | null;
        if (zone) {
          // Find the nearest scrollable ancestor within the zone
          let node: HTMLElement | null = target as HTMLElement;
          let verticalEl: HTMLElement | null = null;
          while (node && zone.contains(node)) {
            const style = window.getComputedStyle(node);
            const canScrollEl = node.scrollHeight > node.clientHeight + 1 && (style.overflowY === 'auto' || style.overflowY === 'scroll');
            if (canScrollEl) {
              verticalEl = node;
              break;
            }
            node = node.parentElement;
          }
          if (!verticalEl) {
            // Fallback: if zone itself scrolls
            const zStyle = window.getComputedStyle(zone);
            if (zone.scrollHeight > zone.clientHeight + 1 && (zStyle.overflowY === 'auto' || zStyle.overflowY === 'scroll')) {
              verticalEl = zone;
            }
          }

          if (verticalEl) {
            const dy = e.deltaY || 0;
            const up = dy < 0;
            const down = dy > 0;
            const canScrollUp = verticalEl.scrollTop > 0;
            const canScrollDown = verticalEl.scrollTop + verticalEl.clientHeight < verticalEl.scrollHeight - 1;
            // If vertical element can continue scrolling in the direction, do not hijack
            if ((up && canScrollUp) || (down && canScrollDown)) {
              return;
            }
            // otherwise, fall through to horizontal handling
          }
        }
      }

      // Calculate intended horizontal delta from wheel
      const delta = (e.deltaX || 0) + (e.deltaY || 0);
      if (delta === 0) return;

      const prev = container.scrollLeft;
      container.scrollLeft = prev + delta;
      const next = container.scrollLeft;

      // If we actually scrolled horizontally, prevent default vertical scroll
      if (next !== prev) {
        e.preventDefault();
      }
    };

    window.addEventListener('wheel', handler, { passive: false });
    return () => window.removeEventListener('wheel', handler);
  }, [viewType, dayModalOpen, editingTodo, createModalOpen, listManagement.showCreateListForm, listManagement.showEditListForm, listManagement.showListInfoDialog]);

  // Calendar calculations
  const calendarDays = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    
    // Adjust for Monday start: getDay() returns 0 for Sunday, 1 for Monday, etc.
    // We want Monday (1) to be position 0, so we use (getDay() + 6) % 7
    const dayOfWeek = (firstDay.getDay() + 6) % 7;
    startDate.setDate(startDate.getDate() - dayOfWeek);
    
    const days = [];
    const current = new Date(startDate);
    
    for (let i = 0; i < 42; i++) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    
    return days;
  }, [currentDate]);

  const weekDays = useMemo(() => {
    const startOfWeek = new Date(currentDate);
    const day = startOfWeek.getDay();
    // Adjust for Monday start: if it's Sunday (0), go back 6 days, otherwise go back (day - 1) days
    const diff = day === 0 ? -6 : -(day - 1);
    startOfWeek.setDate(startOfWeek.getDate() + diff);
    
    const days = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      days.push(date);
    }
    return days;
  }, [currentDate]);

  // Legend will be computed after getTasksForDate is declared

  const getTasksForDate = useCallback((date: Date) => {
    const dateStr = dayjs(date).format('YYYY-MM-DD');
    return todos.filter(todo => {
      if (!todo.dueDate) return false;
      const todoDueDate = dayjs(todo.dueDate).format('YYYY-MM-DD');
      if (todoDueDate !== dateStr) return false;
      
      // Apply filters
      if (todoFilters.list && todoFilters.list !== 'all') {
        if (typeof todoFilters.list === 'number' && todo.listId !== todoFilters.list) {
          return false;
        }
      }
      
      if (todoFilters.assignee && todoFilters.assignee !== 'any') {
        if (todoFilters.assignee === 'unassigned') {
          if (todo.assignedTo && todo.assignedTo.trim() !== '') {
            return false;
          }
        } else {
          if (!todo.assignedTo || todo.assignedTo !== todoFilters.assignee) {
            return false;
          }
        }
      }
      
      if (todoFilters.search) {
        const searchLower = todoFilters.search.toLowerCase();
        if (!todo.title.toLowerCase().includes(searchLower) && 
            !(todo.description || '').toLowerCase().includes(searchLower)) {
          return false;
        }
      }
      
      if (todoFilters.tags && todoFilters.tags.length > 0) {
        let todoTags: string[] = [];
        if (Array.isArray(todo.tags)) {
          todoTags = todo.tags;
        } else if (typeof todo.tags === 'string' && todo.tags) {
          todoTags = (todo.tags as string).split(',').map((t: string) => t.trim().toLowerCase());
        }
        
        const hasMatchingTag = todoFilters.tags.some(filterTag => 
          todoTags.some(todoTag => todoTag.toLowerCase() === filterTag.toLowerCase())
        );
        if (!hasMatchingTag) return false;
      }
      
      return true;
    });
  }, [todos, todoFilters]);

  const handleDragStart = (event: DragStartEvent) => {
    const task = event.active.data.current as TodoItemType;
    setDraggedTask(task);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    // Always clear dragged task immediately
    setDraggedTask(null);

    // Return early if no valid drop
    if (!over || !active.data.current) return;

  const task = active.data.current as TodoItemType;
    const newDate = over.data.current?.date as string | undefined;
    if (!newDate) return;

    // Compare only the day portion to detect same-day drops
    const currentDay = task.dueDate ? dayjs(task.dueDate).format('YYYY-MM-DD') : null;
    if (currentDay === newDate) return;

    // Preserve time if it exists
    const updatedDate =
      task.dueTime && task.dueDate
        ? dayjs(newDate)
            .hour(dayjs(task.dueDate).hour())
            .minute(dayjs(task.dueDate).minute())
            .toISOString()
        : dayjs(newDate).toISOString();

    // Optimistically update all todos caches, regardless of their shape or queryKey variants
    queryClient.setQueriesData({ queryKey: ['todos'] }, (old: any) => {
      if (!old) return old;

      const applyUpdate = (t: any) => (t.id === task.id ? { ...t, dueDate: updatedDate } : t);

      if (Array.isArray(old)) {
        return old.map(applyUpdate);
      }

      if (Array.isArray(old?.data)) {
        return { ...old, data: old.data.map(applyUpdate) };
      }

      if (Array.isArray(old?.pages)) {
        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            data: Array.isArray(page?.data) ? page.data.map(applyUpdate) : page.data,
          })),
        };
      }

      return old;
    });

    // Server update; fallback to invalidate on error
    updateTodoMutation.mutate(
      {
        id: task.id,
        data: { dueDate: updatedDate },
      },
      {
        onError: () => {
          queryClient.invalidateQueries({ queryKey: ['todos'] });
        },
      }
    );
  };

  // Build legend of lists that have visible tasks in current view (month or week)
  const visibleListIds = useMemo(() => {
    const ids = new Set<number>();
    const dates = viewType === 'month' ? calendarDays : weekDays;
    dates.forEach((d) => {
      const tasks = getTasksForDate(d);
      tasks.forEach((t) => ids.add(t.listId));
    });
    return ids;
  }, [viewType, calendarDays, weekDays, getTasksForDate]);

  const visibleListInfos = useMemo(() => {
    if (!visibleListIds.size) return [] as { id: number; name: string; color?: string }[];
    const arr: { id: number; name: string; color?: string }[] = [];
    visibleListIds.forEach((id) => {
      const li = todoLists.find((l) => l.id === id);
      if (li) arr.push({ id: li.id, name: li.name, color: li.color });
    });
    return arr.sort((a, b) => a.name.localeCompare(b.name));
  }, [visibleListIds, todoLists]);

  const navigatePrevious = () => {
    const newDate = new Date(currentDate);
    if (viewType === 'month') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else {
      newDate.setDate(newDate.getDate() - 7);
    }
    setCurrentDate(newDate);
  };

  const navigateNext = () => {
    const newDate = new Date(currentDate);
    if (viewType === 'month') {
      newDate.setMonth(newDate.getMonth() + 1);
    } else {
      newDate.setDate(newDate.getDate() + 7);
    }
    setCurrentDate(newDate);
  };

  const handleDayClick = (date: Date) => {
    setSelectedDate(date);
    setDayModalOpen(true);
  };

  const handleToggleComplete = useCallback((t: TodoItemType) => {
    // Try to infer configured start/done status IDs from the selected list if present
    let startId: string | undefined;
    let doneId: string | undefined;
    if (selectedList && Array.isArray(selectedList.items)) {
      // If any item is marked completed, use its status as doneId (most accurate for this list)
      const anyDone = selectedList.items.find(i => i.isCompleted && i.status);
      const anyStart = selectedList.items.find(i => !i.isCompleted && i.status);
      doneId = (anyDone?.status as string | undefined) || undefined;
      startId = (anyStart?.status as string | undefined) || undefined;
    }

    // Optimistic update across todos caches, including status when we can infer it
    queryClient.setQueriesData({ queryKey: ['todos'] }, (old: any) => {
      if (!old) return old;
      const apply = (x: any) => {
        if (x.id !== t.id) return x;
        const nextCompleted = !t.isCompleted;
        const next: any = { ...x, isCompleted: nextCompleted };
        if (nextCompleted && doneId) next.status = doneId;
        if (!nextCompleted && startId) next.status = startId;
        return next;
      };
      if (Array.isArray(old)) return old.map(apply);
      if (Array.isArray(old?.data)) return { ...old, data: old.data.map(apply) };
      if (Array.isArray(old?.pages)) {
        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            data: Array.isArray(page?.data) ? page.data.map(apply) : page.data,
          })),
        };
      }
      return old;
    });

    // Server update; backend will enforce mapping using configured Types
    updateTodoMutation.mutate({ id: t.id, data: { isCompleted: !t.isCompleted } }, {
      onError: () => queryClient.invalidateQueries({ queryKey: ['todos'] })
    });
  }, [queryClient, updateTodoMutation, selectedList]);

  // Inline add is handled by SectionTodoEntry; modal-based creation removed from week/day cells

  const handleSubmitNewTask = async () => {
    if (!newTask.title.trim() || !newTask.dueDate) return;

    const createData: CreateTodoRequest = {
      title: newTask.title,
      priority: newTask.priority,
      dueDate: newTask.dueDate.toISOString(),
      listId: typeof todoFilters.list === 'number' ? todoFilters.list : undefined
    };

    await createTodoMutation.mutateAsync(createData);

    setCreateModalOpen(false);
    setNewTask({
      title: '',
      priority: 'Medium',
      dueDate: null,
      dueTime: false
    });
  };

  // List management handlers
  const handleCreateList = () => {
    listManagement.handleCreateList();
  };

  const handleEditList = () => {
    if (selectedList) {
      listManagement.handleEditList(selectedList);
    }
  };

  const renderMonthView = () => {
    const weekDayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return (
      <div>
        
        <Grid gutter="xs" mb="xs">
          {weekDayLabels.map(day => (
            <Grid.Col key={day} span={12/7}>
              <Paper
                p="xs"
                style={{
                  backgroundColor: 'var(--app-hover)',
                  border: '1px solid var(--app-border)',
                  borderRadius: 'var(--mantine-radius-sm)'
                }}
              >
                <Text size="sm" fw={600} ta="center" style={{ color: 'var(--app-text-secondary)' }}>
                  {day}
                </Text>
              </Paper>
            </Grid.Col>
          ))}
        </Grid>
        <Grid gutter="xs">
          {calendarDays.map((date, index) => {
            const tasks = getTasksForDate(date);
            const isCurrentMonth = date.getMonth() === currentDate.getMonth();
            const isToday = date.toDateString() === today.toDateString();
            const hasOverdue = tasks.some(t => !t.isCompleted && dayjs(t.dueDate).isBefore(dayjs().startOf('day')));
            
            return (
              <Grid.Col key={index} span={12/7}>
                <DroppableCalendarCell
                  date={date}
                  isCurrentMonth={isCurrentMonth}
                  isToday={isToday}
                  onDayClick={() => handleDayClick(date)}
                >
                  <Group justify="space-between" mb={4}>
                    <Text
                      size="md" // Increased font size
                      fw={isToday ? 800 : 500}
                      style={{
                        color: isToday
                          ? 'var(--app-primary)'
                          : isCurrentMonth
                          ? 'var(--app-text-primary)'
                          : 'var(--app-text-muted)',
                      }}
                    >
                      {date.getDate()}
                    </Text>
                    {hasOverdue && (
                      <Indicator color="red" size={8} processing />
                    )}
                  </Group>
                  <ScrollArea h={120} data-vertical-scroll-zone="true" offsetScrollbars scrollbarSize={SCROLLBAR_GUTTER_PX} type="always">
                    <Box style={{ paddingLeft: SCROLLBAR_GUTTER_PX }}>
                      <Stack gap={4}>
                        {sortTasksForCalendar(tasks).map(task => {
                          const stripeColor = todoFilters.list === 'all'
                            ? (todoLists.find((l: TodoList) => l.id === task.listId)?.color)
                            : undefined;
                          return (
                            <DraggableTask
                              key={task.id}
                              task={task}
                              compact
                              onOpenEdit={(t) => setEditingTodo(t)}
                              onToggleComplete={handleToggleComplete}
                              showCheckbox={true} // Now showing checkbox
                              stripeColor={stripeColor}
                            />
                          );
                        })}
                      </Stack>
                    </Box>
                  </ScrollArea>
                </DroppableCalendarCell>
              </Grid.Col>
            );
          })}
        </Grid>
      </div>
    );
  };

  const renderWeekView = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return (
  <Box ref={weekScrollRef} style={{ overflowX: 'auto', paddingBottom: 8 }}>
        <Group gap="md" wrap="nowrap" align="stretch" style={{ width: 'max-content', margin: '0 auto' }}>
          {weekDays.map((date, index) => {
          const tasks = getTasksForDate(date);
          const isToday = date.toDateString() === today.toDateString();
          
          return (
            <DroppableWeekDayCell
              key={index}
              date={date}
              isToday={isToday}
              tasks={tasks}
              onOpenEdit={(t) => setEditingTodo(t)}
              onToggleComplete={handleToggleComplete}
              currentFilters={todoFilters}
              onFiltersChange={setTodoFilters}
                onDayClick={(d) => handleDayClick(d)}
              todoLists={todoLists}
            />
          );
          })}
        </Group>
      </Box>
    );
  };

  // Handle upgrade button click
  const handleUpgradeClick = () => {
  // Navigate to Subscription Plan tab in Settings (same tab)
  window.location.href = '/settings?tab=plan';
  };

  // Show loading state while premium status is being determined
  if (premiumLoading) {
    return (
      <div style={{ 
        height: '100vh',
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'var(--app-bg)'
      }}>
        <Text size="lg" style={{ color: 'var(--app-text-primary)' }}>
          Loading Calendar...
        </Text>
      </div>
    );
  }

  // Mobile restriction: Calendar only works on bigger screens
  if (isMobile) {
    return (
      <div style={{ 
        minHeight: '100vh',
        display: 'flex', 
        flexDirection: 'column',
        backgroundColor: 'var(--app-bg)',
        padding: '20px',
        paddingTop: '60px', // Move content higher up
        textAlign: 'center'
      }}>
        <Stack align="center" gap="md" style={{ maxWidth: '400px', margin: '0 auto' }}>
          <Text size="xl" fw={600} style={{ color: 'var(--app-text-primary)' }}>
            📱 Calendar View
          </Text>
          
          {isPremium ? (
            <>
              <Text size="lg" fw={500} style={{ color: 'var(--app-text-secondary)', marginBottom: '8px' }}>
                Calendar view only works on <br></br> bigger screens (for now)
              </Text>
              <Text size="sm" style={{ color: 'var(--app-text-muted)', lineHeight: 1.5 }}>
                Please use a desktop or tablet with a larger display to access the full calendar experience. I'm still experimenting with mobile layouts, and suggestions are welcome for how you might want to use it on a smaller screen!
              </Text>
            </>
          ) : (
            <>
              <Text size="lg" fw={500} style={{ color: 'var(--app-text-secondary)', marginBottom: '8px' }}>
                Calendar view is a premium feature
              </Text>
              <Text size="sm" style={{ color: 'var(--app-text-muted)', lineHeight: 1.5, marginBottom: '16px' }}>
                Upgrade to premium and use a bigger screen to access the full calendar experience.
              </Text>
              <Button 
                size="lg"
                onClick={handleUpgradeClick}
              >
                Upgrade to Premium
              </Button>
            </>
          )}
        </Stack>
      </div>
    );
  }

  // Show demo for non-premium users
  if (!isPremium) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'var(--app-bg)'
      }}>
        {/* Demo Content */}
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          maxWidth: '800px',
          margin: '0 auto',
          width: '100%'
        }}>
          <div style={{
            textAlign: 'center',
            marginBottom: '24px',
          }}>
            <Text size="xl" fw={600} style={{ color: 'var(--app-text-primary)', marginBottom: '8px' }}>
              📅 Calendar View Preview
            </Text>
            <Text size="sm" style={{ color: 'var(--app-text-secondary)' }}>
              Try our interactive demo and upgrade for unlimited Calendar power
            </Text>
          </div>

          <CalendarPreviewDemo onUpgradeClick={handleUpgradeClick} />
        </div>
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={(args) => {
        const byPointer = pointerWithin(args);
        if (byPointer.length > 0) return byPointer;
        return rectIntersection(args);
      }}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      measuring={{
        droppable: {
          strategy: MeasuringStrategy.Always,
        },
      }}
      autoScroll={false}
    >
      <div style={{ 
        height: '100vh',
        minHeight: 0,
        display: 'flex', 
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        {/* Shared Header with Filters */}
        <div style={{ flexShrink: 0, padding: '20px 20px 0 20px' }}>
          <SuperTaskToolbar
            onFilterChange={setTodoFilters}
            currentFilters={todoFilters}
            selectedList={selectedList}
            onCreateList={handleCreateList}
            onEditList={handleEditList}
          />
        </div>
        
        {/* Calendar Content */}
  <div style={{ flex: 1, padding: '0 20px 20px 20px', overflow: 'hidden', minHeight: 0 }}>
          <ScrollArea h="100%" type="always" offsetScrollbars scrollbarSize={SCROLLBAR_GUTTER_PX} data-vertical-scroll-zone="true">
            <Box style={{ paddingLeft: SCROLLBAR_GUTTER_PX, minHeight: 0 }}>
              <Stack gap="lg">
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <Stack gap={8} align="center">
                    <Card withBorder p="xs" radius="md" shadow="xs" style={{ width: 'fit-content', maxWidth: '100%' }}>
                      <Box style={{ overflowX: 'auto' }}>
                        <Group gap="xs" align="center" wrap="nowrap" style={{ whiteSpace: 'nowrap' }}>
                          <ActionIcon size="sm" onClick={navigatePrevious} variant="default">
                            <ChevronLeft size={16} />
                          </ActionIcon>
                          <Text fw={600} style={{ textAlign: 'center', margin: 0 }} size="sm">
                            {viewType === 'month' 
                              ? currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
                              : `Week of ${weekDays[0]?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) || ''}`
                            }
                          </Text>
                          <ActionIcon size="sm" onClick={navigateNext} variant="default">
                            <ChevronRight size={16} />
                          </ActionIcon>
                          <Button 
                            variant="light" 
                            size="xs"
                            onClick={() => setCurrentDate(new Date())}
                          >
                            Today
                          </Button>
                          <SegmentedControl
                            size="xs"
                            radius="sm"
                            value={viewType}
                            onChange={(value) => setViewType(value as ViewType)}
                            data={[
                              { label: 'Month', value: 'month' },
                              { label: 'Week', value: 'week' },
                            ]}
                          />
                        </Group>
                      </Box>
                    </Card>

                    {todoFilters.list === 'all' && visibleListInfos.length > 0 && (
                      <Paper
                        p="sm"
                        radius="md"
                        withBorder
                        style={{
                          backgroundColor: 'var(--app-surface)',
                          border: '1px solid var(--app-border)',
                          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                        }}
                      >
                        <Group gap={8} justify="center" wrap="wrap" maw="100%">
                          {visibleListInfos.map((li) => (
                            <Group key={li.id} gap={6} wrap="nowrap">
                              <span
                                aria-hidden
                                style={{
                                  display: 'inline-block',
                                  width: 10,
                                  height: 10,
                                  borderRadius: 2,
                                  backgroundColor: li.color || 'var(--app-text-muted)',
                                  border: '1px solid var(--app-border)'
                                }}
                              />
                              <Text size="xs" style={{ color: 'var(--app-text-primary)' }}>{li.name}</Text>
                            </Group>
                          ))}
                        </Group>
                      </Paper>
                    )}
                  </Stack>
                </div>

                <Box style={{ minHeight: 0 }}>
                  {viewType === 'month' ? (
                    renderMonthView()
                  ) : (
                    <ScrollArea h="100%" type="always" offsetScrollbars scrollbarSize={SCROLLBAR_GUTTER_PX} data-vertical-scroll-zone="true">
                      <Box style={{ paddingLeft: SCROLLBAR_GUTTER_PX }}>
                        {renderWeekView()}
                      </Box>
                    </ScrollArea>
                  )}
                </Box>
              </Stack>
            </Box>
          </ScrollArea>
        </div>

        {/* Day Detail Modal */}
        <Modal
          opened={dayModalOpen}
          onClose={() => setDayModalOpen(false)}
          title={selectedDate?.toLocaleDateString('en-US', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          })}
          size="md"
          styles={{
            content: {
              backgroundColor: 'var(--app-surface)',
              border: '1px solid var(--app-border)'
            },
            header: {
              backgroundColor: 'var(--app-surface)',
              borderBottom: '1px solid var(--app-border)',
              color: 'var(--app-text-primary)'
            },
            title: {
              color: 'var(--app-text-primary)',
              fontWeight: 600
            },
            close: {
              color: 'var(--app-text-secondary)',
              '&:hover': {
                backgroundColor: 'var(--app-hover)'
              }
            }
          }}
        >
          {selectedDate && (
            <Stack>
              <ScrollArea h={400} data-vertical-scroll-zone="true" offsetScrollbars scrollbarSize={SCROLLBAR_GUTTER_PX} type="always">
                <Box style={{ paddingLeft: SCROLLBAR_GUTTER_PX }}>
                  <Stack gap="sm">
                    {sortTasksForCalendar(getTasksForDate(selectedDate))
                      .map(task => (
                        // Reuse the shared card to enable editing inside the modal
                        <TodoItemComponent 
                          key={task.id} 
                          todo={task}
                          onEdit={() => setEditingTodo(task)}
                        />
                      ))}
                    {getTasksForDate(selectedDate).length === 0 && (
                      <Text style={{ color: 'var(--app-text-muted)' }} ta="center">No tasks for this day</Text>
                    )}
                  </Stack>
                </Box>
              </ScrollArea>
              <div style={{ marginTop: 8 }}>
                {/* Reuse the exact inline entry component from TodoList with dotted border styling */}
                <SectionTodoEntry 
                  sectionType="noDueDate"
                  currentFilters={todoFilters}
                  onFiltersChange={(f: { assignee: string; list: string | number; completed: 'today' | 'all' | 'hidden'; tags: string[]; search: string }) => setTodoFilters(f)}
                  hideIcon={true}
                  dueDateISO={dayjs(selectedDate).format('YYYY-MM-DD')}
                />
              </div>
            </Stack>
          )}
        </Modal>

        {/* Edit Task Modal (overlays above the day popup) */}
        {editingTodo && (
          <CustomModal
            isOpen={!!editingTodo}
            onClose={() => {
              setEditingTodo(null);
              setEditFormData(null);
            }}
            hideCloseButton={true}
            headerContent={
              <TaskTitleInput
                value={editingTodo.title}
                onChange={(value) => {
                  setEditingTodo(prev => prev ? { ...prev, title: value } : null);
                  setEditFormData(prev => prev ? { ...prev, title: value } : { ...editingTodo, title: value });
                }}
                autoFocus={true}
                isCompleted={editingTodo.isCompleted}
                onToggleComplete={() => {
                  const newCompletedState = !editingTodo.isCompleted;
                  // Update local state immediately
                  setEditingTodo(prev => prev ? { ...prev, isCompleted: newCompletedState } : null);
                  // Update in backend
                  updateTodoMutation.mutate({
                    id: editingTodo.id,
                    data: { isCompleted: newCompletedState }
                  }, {
                    onSuccess: () => {
                      // Close modal after longer delay if marking as complete
                      if (newCompletedState) {
                        setTimeout(() => {
                          setEditingTodo(null);
                          setEditFormData(null);
                        }, 600);
                      }
                    }
                  });
                }}
              />
            }
            actionButtons={
              <>
                <button
                  type="button"
                  onClick={() => {
                    if (editFormData && editingTodo && editingTodo.id > 0) {
                      updateTodoMutation.mutate({ id: editingTodo.id, data: editFormData }, {
                        onSuccess: () => {
                          setEditingTodo(null);
                          setEditFormData(null);
                        }
                      });
                    }
                  }}
                  disabled={!editFormData || !editFormData.title?.trim() || updateTodoMutation.isPending || (editingTodo && editingTodo.id < 0)}
                  className="header-btn header-btn-save"
                  title={editingTodo && editingTodo.id < 0 ? "Please wait for task to be created..." : "Save task"}
                  aria-label="Save task"
                >
                  <Check size={16} />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (editingTodo) {
                      deleteTodoMutation.mutate(editingTodo.id, {
                        onSuccess: () => {
                          setEditingTodo(null);
                          setEditFormData(null);
                        }
                      });
                    }
                  }}
                  disabled={deleteTodoMutation.isPending}
                  className="header-btn header-btn-delete"
                  title="Delete task"
                  aria-label="Delete task"
                >
                  <Trash2 size={16} />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEditingTodo(null);
                    setEditFormData(null);
                  }}
                  className="header-btn header-btn-close"
                  title="Cancel"
                  aria-label="Cancel"
                >
                  <X size={16} />
                </button>
              </>
            }
          >
            <EditTodoForm
              todo={editingTodo}
              onComplete={() => {
                setEditingTodo(null);
                setEditFormData(null);
              }}
              onDataChange={setEditFormData}
              renderTitleInline={false}
            />
          </CustomModal>
        )}

        {/* Create Task Modal */}
        <Modal
          opened={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          title="Create New Task"
          size="md"
          styles={{
            content: {
              backgroundColor: 'var(--app-surface)',
              border: '1px solid var(--app-border)'
            },
            header: {
              backgroundColor: 'var(--app-surface)',
              borderBottom: '1px solid var(--app-border)',
              color: 'var(--app-text-primary)'
            },
            title: {
              color: 'var(--app-text-primary)',
              fontWeight: 600
            },
            close: {
              color: 'var(--app-text-secondary)',
              '&:hover': {
                backgroundColor: 'var(--app-hover)'
              }
            }
          }}
        >
          <Stack>
            <TextInput
              label="Task Title"
              placeholder="Enter task title"
              value={newTask.title}
              onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
              required
            />
            <Select
              label="Priority"
              value={newTask.priority}
              onChange={(value) => setNewTask({ ...newTask, priority: value as 'High' | 'Medium' | 'Low' })}
              data={[
                { value: 'High', label: 'High' },
                { value: 'Medium', label: 'Medium' },
                { value: 'Low', label: 'Low' },
              ]}
            />
            <DateTimePicker
              label="Due Date & Time"
              placeholder="Pick date and time"
              value={newTask.dueDate}
              onChange={(value) => {
                setNewTask({ 
                  ...newTask, 
                  dueDate: value ? new Date(value) : null,
                  dueTime: value ? true : false
                });
              }}
              clearable
            />
            <Group justify="flex-end">
              <Button variant="subtle" onClick={() => setCreateModalOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSubmitNewTask}
                disabled={!newTask.title.trim() || !newTask.dueDate}
              >
                Create Task
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Centralized List Management Modals */}
        <ListManagementModals listManagement={listManagement} />

        <DragOverlay>
          {draggedTask && (
            <div style={{ 
              transform: 'rotate(1deg)',
              opacity: 0.95,
              boxShadow: '0 10px 25px var(--app-text-muted)',
              width: 480,
              maxWidth: '95vw',
              minWidth: 280,
              maxHeight: 110,
              overflow: 'hidden',
              pointerEvents: 'none',
              userSelect: 'none'
            }}>
              <TodoItemComponent
                todo={draggedTask}
                onEdit={() => {}}
                dragHandleProps={null}
                dragAttributes={null}
                dragListeners={null}
                sortType="rank"
              />
            </div>
          )}
        </DragOverlay>
      </div>
    </DndContext>
  );
};


